import { useState } from 'react';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

const B = { primary: '#4357AD', dark: '#1E2A4A' };

const MONTHS = ['Січ', 'Лют', 'Бер', 'Кві', 'Тра', 'Чер', 'Лип', 'Сер', 'Вер', 'Жов', 'Лис', 'Гру'];

/**
 * PeriodSelector - unified period picker
 * @param {string} mode - 'week' | 'month'
 * @param {string} periodStart - ISO date
 * @param {string} periodEnd - ISO date
 * @param {function} onChange - (start, end, label) => void
 */
const PeriodSelector = ({ mode = 'month', periodStart, periodEnd, onChange }) => {
  const [customFrom, setCustomFrom] = useState(null);
  const [customTo, setCustomTo] = useState(null);

  const year = dayjs().year();
  const currentMonth = dayjs().month();

  const handleMonth = (monthIdx) => {
    const start = dayjs().year(year).month(monthIdx).startOf('month');
    const end = start.endOf('month');
    onChange(start.format('YYYY-MM-DD'), end.format('YYYY-MM-DD'), `${MONTHS[monthIdx]} ${year}`);
  };

  const handleWeek = (offset) => {
    const base = dayjs().add(offset, 'week').startOf('week');
    const end = base.add(6, 'day');
    const label = offset === 0 ? 'Поточний тиждень' : offset === -1 ? 'Минулий тиждень' : `${base.format('DD.MM')}–${end.format('DD.MM')}`;
    onChange(base.format('YYYY-MM-DD'), end.format('YYYY-MM-DD'), label);
  };

  const handleCustom = () => {
    if (customFrom && customTo && customFrom.isValid() && customTo.isValid()) {
      onChange(customFrom.format('YYYY-MM-DD'), customTo.format('YYYY-MM-DD'), `${customFrom.format('DD.MM.YY')}–${customTo.format('DD.MM.YY')}`);
    }
  };

  const isActive = (start, end) => periodStart === start && periodEnd === end;

  return (
    <Box sx={{ px: 2.5, py: 2, borderRadius: 3, bgcolor: '#F8F7FF', border: '1px solid #EDE9FE' }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <CalendarTodayIcon sx={{ fontSize: 16, color: B.primary }} />
        <Typography variant="subtitle2" fontWeight={700} sx={{ color: B.dark }}>Звітний період</Typography>
      </Stack>

      {/* Month buttons */}
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
        {MONTHS.map((label, idx) => {
          const start = dayjs().year(year).month(idx).startOf('month').format('YYYY-MM-DD');
          const end = dayjs().year(year).month(idx).endOf('month').format('YYYY-MM-DD');
          const active = isActive(start, end);
          return (
            <Chip
              key={idx}
              label={label}
              size="small"
              onClick={() => handleMonth(idx)}
              sx={{
                fontWeight: 600, fontSize: 12, cursor: 'pointer',
                bgcolor: active ? B.primary : idx === currentMonth ? '#EDE9FE' : '#fff',
                color: active ? '#fff' : B.dark,
                border: active ? `2px solid ${B.primary}` : '1px solid #EDE9FE',
                '&:hover': { bgcolor: active ? B.primary : '#EDE9FE' },
              }}
            />
          );
        })}
      </Stack>

      {/* Week buttons (only for week mode) */}
      {mode === 'week' && (
        <Stack direction="row" spacing={0.5} sx={{ mb: 1.5 }}>
          {[
            { offset: 0, label: 'Поточний тиждень' },
            { offset: -1, label: 'Минулий тиждень' },
          ].map(({ offset, label }) => {
            const base = dayjs().add(offset, 'week').startOf('week');
            const end = base.add(6, 'day');
            const start = base.format('YYYY-MM-DD');
            const endStr = end.format('YYYY-MM-DD');
            const active = isActive(start, endStr);
            return (
              <Chip
                key={offset}
                label={label}
                size="small"
                onClick={() => handleWeek(offset)}
                sx={{
                  fontWeight: 600, fontSize: 12, cursor: 'pointer',
                  bgcolor: active ? B.primary : '#fff',
                  color: active ? '#fff' : '#8891A5',
                  border: active ? `2px solid ${B.primary}` : '1px solid #EDE9FE',
                  '&:hover': { bgcolor: active ? B.primary : '#EDE9FE' },
                }}
              />
            );
          })}
        </Stack>
      )}

      {/* Custom range */}
      <Stack direction="row" spacing={1.5} alignItems="center">
        <DatePicker
          label="Від"
          value={customFrom}
          onChange={setCustomFrom}
          format="DD.MM.YYYY"
          slotProps={{ textField: { size: 'small', sx: { flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 2, '& fieldset': { borderColor: '#EDE9FE' } } } } }}
        />
        <DatePicker
          label="До"
          value={customTo}
          onChange={setCustomTo}
          format="DD.MM.YYYY"
          slotProps={{ textField: { size: 'small', sx: { flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 2, '& fieldset': { borderColor: '#EDE9FE' } } } } }}
        />
        <Button
          size="small" variant="outlined" onClick={handleCustom}
          disabled={!customFrom || !customTo}
          sx={{ textTransform: 'none', borderRadius: 2, borderColor: '#EDE9FE', color: B.primary, fontWeight: 600, px: 3, minHeight: 40, '&:hover': { borderColor: B.primary } }}
        >
          Застосувати
        </Button>
      </Stack>
    </Box>
  );
};

export default PeriodSelector;
